import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { apiError } from "@/lib/errors";
import { roleLabel } from "@/context/AuthContext";
import { useOrgs } from "@/context/OrgsContext";
import { UserAssignmentFields } from "@/components/admin/ScopeFields";

const EMPTY = { email: "", name: "", password: "", role: "school_admin", must_change_password: true };

const Step = ({ n, title, children }) => (
  <div className="space-y-2">
    <p className="font-overline text-[10px] text-[#E11D48]">Step {n} · {title}</p>
    {children}
  </div>
);

export default function NewUserForm({ roles, sports, onCreated, title = "ADD USER", testid = "new-user-form" }) {
  const { orgs } = useOrgs();
  const emptyScope = { association_id: orgs.default_association_id || "", school_id: "", sport_ids: [] };
  const [form, setForm] = useState({ ...EMPTY, role: roles.includes("school_admin") ? "school_admin" : roles[0] });
  const [scope, setScope] = useState(emptyScope);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, ...scope, association_id: scope.association_id || null, school_id: scope.school_id || null });
      toast.success(`${form.name} onboarded`);
      setForm({ ...EMPTY, role: form.role }); setScope(emptyScope); onCreated();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <form onSubmit={create} className="border border-slate-300 bg-white p-4 space-y-4 h-fit" data-testid={testid}>
      <h3 className="font-heading text-xl">{title}</h3>
      <Step n={1} title="Role">
        <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
          <SelectTrigger className="rounded-sm" data-testid="user-role"><SelectValue/></SelectTrigger>
          <SelectContent>{roles.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
        </Select>
      </Step>
      <Step n={2} title="Association & School">
        <UserAssignmentFields role={form.role} value={scope} onChange={setScope} sports={sports} testidPrefix="user-scope"/>
        <p className="text-[11px] text-slate-500">Type a new name in either field to create it — it's instantly available everywhere.</p>
      </Step>
      <Step n={3} title="Account">
        <Input placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm" required data-testid="user-name"/>
        <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-sm" required data-testid="user-email"/>
        <div>
          <Label className="text-xs">Initial password (min 6)</Label>
          <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-sm font-mono" required data-testid="user-password"/>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={form.must_change_password} onCheckedChange={v => setForm({ ...form, must_change_password: !!v })} data-testid="user-force-change"/>
          Require password change at first login
        </label>
      </Step>
      <Button type="submit" className="w-full bg-[#0A1128] hover:bg-slate-800 rounded-sm font-heading" data-testid="create-user-btn">CREATE ACCOUNT</Button>
    </form>
  );
}
